import React from "react";
import { Testimonial, TestimonialsComponent } from "../components/common/testimonial/TestimonialComponent";
import { SEOHelmet } from "../components/common/SEOHelmet";
import { PATH_NEUROLOGIE_GERIATRIE } from "../utils/constants";

type Section = {
  title: string;
  content: string[];
  texts?: string[];
  img?: string;
  ratio?: string;
};

const data: { sections: Section[]; testimonials: Testimonial[] } = {
  sections: [
    {
      title: "Neurologie / Geriatrie",
      content: [
        "Neurologische Erkrankungen:",
        "Schlaganfälle/Blutungen",
        "neurologische Störungen bedingt durch Tumore oder Traumata",
        "Multiple Sklerose",
        "ALS",
        "Morbus Parkinson",
        "Demenz",
        "Kognitive Defizite",
        "Post-Covid",
      ],
      img: process.env.PUBLIC_URL + "/images/neurology.jpg",
      ratio: "aspect-[3/4]",
      texts: [
        "Nach einem Schicksalsschlag wie beispielsweise einem Schlaganfall ist nichts mehr wie es war. Ergotherapie setzt genau da an. Wir unterstützen und begleiten Sie, greifen auf , was Ihnen schwerfällt und erarbeiten gemeinsame Therapieziele. Mögliche Fragen könnten sein: Was ist Ihnen wichtig? Was möchten Sie im Alltag wieder  können? Welche Unterstützungsmöglichkeiten gibt es?",
        "Angehörige oder enge Bezugspersonen können mit in die Therapie einbezogen werden. Selbstverständlich wird dafür im Vorfeld das Einverständnis des Klienten:In eingeholt.",
      ],
    },
    {
      title: "Was beinhaltet eine sensomotorisch-perzeptive Behandlung?",
      content: [
        "Verbesserung der Grob- und Feinmotorik",
        "Verbesserung von alltäglichen Verrichtungen (z.B. Zähne putzen, anziehen etc.)",
        "Verbesserung der Sensomotorik (Tonusregulation, Anbahnen physiologischer Bewegungen, Koordination von Bewegungsabläufen)",
        "Erarbeiten der individuellen Grundmobilität",
        "Sensibilitätstraining",
        "Propriozeptive Neuromuskuläre Fazilitation bei Gesichtslähmung",
        "Graphomotorisches Training",
      ],
    },
    {
      title: "Was wird bei einem Hirnleistungstraining beübt?",
      content: [
        "Verbesserung der Konzentrations- und Aufmerksamkeitsfähigkeit",
        "Erarbeiten von Gedächtnisstrategien",
        "Einbeziehung von Angehörigen/nahen Bezugspersonen, um auf Hilfestellung/Einbindung im häuslichen Umfeld hinzuweisen",
        "Beratungen bezüglich Gefahren im Alltag, Hinweis auf unterstützende Organisationen",
        "Erhaltung und Verbesserung der Merkfähigkeit, Leistungen im auditiven, sowie im visuellen Bereich",
        "Schulung der Orientierungsfähigkeit (zeitl., örtl., persönl., situativ)",
      ],
    },
    {
      title: "Was beinhaltet ein ADL-Training?",
      content: [
        "Wasch- und Anziehtraining",
        "Nahrungsaufnahme",
        "Haushaltstraining",
        "Hilfsmittelberatung und Hilfsmittelversorgung",
      ],
    },
    {
      title: "Was noch berücksichtigt werden sollte?",
      content: [
        "Stärkung und Förderung des Selbstwertgefühls",
        "Verbesserung der sozio-emotionalen Fähigkeiten",
        "Stärkung im Bereich: Kommunikationsfähigkeiten",
        "Umgang innerhalb der Partnerschaft",
        "Umgang mit dem Thema Sexualität",
        "Kontinenz",
        "Angehörigenberatung",
      ],
    },
  ],
  testimonials: [
    {
      quote:
        "Dank der Ergotherapie bin ich wieder in der Lage, meine betroffene Hand besser zu spüren. Ich kann wieder ein Wasserglas halten und selbständig trinken.",
    },
    {
      quote:
        "In der Praxis habe ich wertvolle Tipps erhalten, um mit der Demenz meines Mannes besser umgehen zu können.Ich kann auch als Angehörige auftretende Probleme innerhalb der Therapie einbringen, damit  gemeinsam Lösungen erarbeitet werden können.",
    },
  ],
};

export default function NeurologyGeriatricsPage() {
  return (
    <>
      <SEOHelmet page="neurologie" pathname={PATH_NEUROLOGIE_GERIATRIE} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalBusiness",
          "name": "Ergotherapie Rottweil - Lisa Spreitzer",
          "description": "Spezialisierte neurologische und geriatrische Ergotherapie in Rottweil für Schlaganfall, MS, Parkinson sowie altersbedingte Einschränkungen.",
          "url": "https://ergorw.de/neurologie-geriatrie",
          "telephone": "+49-741-89698293",
          "email": "info@ergorw.de",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Auf dem Wall 29",
            "addressLocality": "Rottweil",
            "addressRegion": "Baden-Württemberg",
            "postalCode": "78628",
            "addressCountry": "DE"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "48.1684",
            "longitude": "8.6278"
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "08:00",
              "closes": "18:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Saturday",
              "opens": "07:00",
              "closes": "17:00"
            }
          ],
          "founder": {
            "@type": "Person",
            "name": "Lisa Spreitzer",
            "jobTitle": "Ergotherapeutin"
          },
          "medicalSpecialty": ["Neurologic", "Geriatric"],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Neurologische und Geriatrische Ergotherapie",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "MedicalTherapy",
                  "name": "Behandlung nach Schlaganfall",
                  "description": "Motorisch-funktionelle Behandlung und Hirnleistungstraining nach Schlaganfall"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "MedicalTherapy",
                  "name": "Multiple Sklerose Therapie",
                  "description": "Ergotherapeutische Behandlung bei MS"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "MedicalTherapy",
                  "name": "Parkinson Behandlung",
                  "description": "Therapie bei Morbus Parkinson"
                }
              }
            ]
          },
          "areaServed": {
            "@type": "City",
            "name": "Rottweil"
          },
          "sameAs": [
            "https://www.instagram.com/ergotherapie_zeittraeume/"
          ]
        })}
      </script>


      <main className="space-y-14">
        {data.sections.map((section, index) => (
          <article key={index}>
            {index === 0 ? (
              // First section with image
              <section className="grid lg:grid-cols-2 gap-12 items-start">
                <div>
                  <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6">
                    Neurologische und Geriatrische Ergotherapie
                  </h1>
                  <h2 className="sr-only">Behandlungsschwerpunkte</h2>
                  <ul
                    className="list-disc marker:text-ergo-400 dark:marker:text-ergo-500 list-inside space-y-3 mb-8 text-base text-gray-500 dark:text-gray-400"
                    aria-label="Neurologische Erkrankungen"
                  >
                    {section.content.map((item, index) => (
                      <li key={index} className="text-gray-500 dark:text-gray-400 leading-relaxed">
                        {item}
                      </li>
                    ))}
                  </ul>
                  {section.texts?.map((text, index) => (
                    <p key={index} className="text-base text-gray-500 dark:text-gray-400 leading-relaxed mb-6">
                      {text}
                    </p>
                  ))}
                </div>
                {section.img && (
                  <aside>
                    <img
                      src={section.img}
                      alt="Neurologische Ergotherapie Rottweil - Behandlung von Schlaganfall, Parkinson, Multiple Sklerose und altersbedingten Einschränkungen"
                      className={`w-64 md:w-96 lg:w-124 rounded-2xl object-cover shadow-sm dark:shadow-xl dark:shadow-black/20 ${section.ratio || ""
                        }`}
                    />
                  </aside>
                )}
              </section>
            ) : (
              // Treatment sections
              <section aria-labelledby={`section-${index}`}>
                <h2
                  id={`section-${index}`}
                  className="text-xl sm:text-2xl font-semibold tracking-tight text-ergo-600 dark:text-ergo-400 mb-4"
                >
                  {section.title}
                </h2>
                <ul className="list-disc marker:text-ergo-400 dark:marker:text-ergo-500 list-inside space-y-3 mb-8 text-base text-gray-500 dark:text-gray-400">
                  {section.content.map((item, index) => (
                    <li key={index} className="text-gray-500 dark:text-gray-400 leading-relaxed">
                      {item}
                    </li>
                  ))}
                </ul>
                {section.texts?.map((text, index) => (
                  <p key={index} className="text-base text-gray-500 dark:text-gray-400 leading-relaxed mb-6">
                    {text}
                  </p>
                ))}
              </section>
            )}
          </article>
        ))}

        {/* Testimonials Section */}
        <section aria-label="Erfahrungsberichte">
          <h2 className="sr-only">Erfahrungsberichte unserer Klienten</h2>
          <TestimonialsComponent testimonials={data.testimonials} />
        </section>
      </main>
    </>
  );
}
